import { UIEvent, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  AppSidebar,
  HrmTopBar,
  useGetNotifications,
  useNotificationSubscription,
} from '@/components/core';

type NotificationsData = {
  notifications: unknown[];
  unReadNotificationsCount: number;
  totalNotificationsCount: number;
};

export const MainLayout = () => {
  // The bell's unread dot updates live: new notifications refresh the query below.
  useNotificationSubscription();
  const { data: notificationsData } = useGetNotifications({
    Page: 0,
    PageSize: 10,
  });
  const { pathname } = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  // The top bar gets its shadow once the page has scrolled (Component Map › Top bar); a new page
  // starts unscrolled.
  useEffect(() => setIsScrolled(false), [pathname]);
  const onScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.parentElement === event.currentTarget) setIsScrolled(target.scrollTop > 4);
  }, []);

  const notifications: NotificationsData = notificationsData ?? {
    notifications: [],
    unReadNotificationsCount: 0,
    totalNotificationsCount: 0,
  };

  return (
    // `fixed inset-0` pins the shell to the viewport, so the top bar never moves even when a gesture
    // reaches the document (scroll chaining, mobile toolbars). It also makes the shell the containing
    // block for absolutely positioned descendants (Radix's hidden form inputs, sr-only text, ...), so
    // they can't escape `overflow-hidden` and stretch the document.
    <div className="fixed inset-0 flex overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <HrmTopBar notifications={notifications} isScrolled={isScrolled} />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
          {/* The page root is the scroll container, so its scrollbar sits on the far right edge. Its
              content is at most 1360px wide and centred (Component Map › Page width): the side padding
              grows to centre it, never below 16px; 24px above, 64px below. */}
          <div
            onScrollCapture={onScroll}
            className="flex h-full min-h-0 min-w-0 flex-1 flex-col [&>*]:flex [&>*]:h-full [&>*]:min-h-0 [&>*]:min-w-0 [&>*]:flex-1 [&>*]:flex-col [&>*]:overflow-auto [&>*]:overscroll-contain [&>*]:px-[max(1rem,calc((100%-1360px)/2))] [&>*]:pb-16 [&>*]:pt-6 [&>*]:hrm-scrollbar"
          >
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};
